import { Input } from '@tark/shared/components';
import type { CompetitionOrganizerEntry } from '@tark/shared/lib';
import { Search } from 'lucide-react';
import type { OrganizerFilters } from './organizerCompetitionUi';
import { formatCompetitionCode } from './organizerCompetitionUi';

type OrganizerEntrantFiltersProps = {
  filters: OrganizerFilters;
  entries: CompetitionOrganizerEntry[];
  onChange: (filters: OrganizerFilters) => void;
};

const selectClassName =
  'h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30';

export default function OrganizerEntrantFilters({
  filters,
  entries,
  onChange,
}: OrganizerEntrantFiltersProps) {
  const divisions = Array.from(
    new Map(entries.map((entry) => [entry.divisionPublicId, entry.divisionName])).entries()
  ).sort((left, right) => left[1].localeCompare(right[1]));

  const statuses = Array.from(new Set(entries.map((entry) => entry.status))).sort();

  return (
    <div className="grid gap-3 border-b border-border bg-surface/40 p-4 md:grid-cols-[minmax(220px,1fr)_180px_160px]">
      <Input
        aria-label="Search entrants"
        value={filters.search}
        onChange={(event) => onChange({ ...filters, search: event.target.value })}
        leftIcon={<Search className="h-4 w-4" />}
        placeholder="Search player, level, or area"
      />

      <label className="space-y-1">
        <span className="sr-only">Division</span>
        <select
          aria-label="Division"
          className={selectClassName}
          value={filters.divisionPublicId}
          onChange={(event) => onChange({ ...filters, divisionPublicId: event.target.value })}
        >
          <option value="">All divisions</option>
          {divisions.map(([divisionPublicId, name]) => (
            <option key={divisionPublicId} value={divisionPublicId}>{name}</option>
          ))}
        </select>
      </label>

      <label className="space-y-1">
        <span className="sr-only">Status</span>
        <select
          aria-label="Status"
          className={selectClassName}
          value={filters.status}
          onChange={(event) => onChange({ ...filters, status: event.target.value })}
        >
          <option value="">All statuses</option>
          {statuses.map((status) => (
            <option key={status} value={status}>{formatCompetitionCode(status)}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
